import { apiClient } from "@/api/client";
import type { ApiResponse } from "@/api/types";

/** Decision #21 — the in-app assistant. Never carries a key back from the server. */
export type AssistantProvider = "ANTHROPIC" | "OPENAI";

export interface AssistantStatus {
  workspaceEnabled: boolean;
  byokAllowed: boolean;
  ready: boolean;
  reason: string | null;
  using: "OWN_KEY" | "COMPANY_KEY" | null;
  connection: { provider: AssistantProvider; model: string; last4: string; lastUsedOn: string | null; connectedOn: string } | null;
  company: { limit: number; used: number } | null;
  providers: Array<{ key: AssistantProvider; label: string; defaultModel: string | null }>;
}

export interface ChatTurn {
  role: "user" | "assistant";
  text: string;
}

export interface ChatReply {
  reply: string;
  tools: Array<{ name: string; ok: boolean }>;
  usage: { inputTokens: number; outputTokens: number; rounds: number; source: "OWN_KEY" | "COMPANY_KEY"; provider: AssistantProvider; model: string };
}

export interface AssistantAdminSettings {
  enabled: boolean;
  byokAllowed: boolean;
  companyKeyEnabled: boolean;
  companyKey: { provider: AssistantProvider; model: string; last4: string; setOn: string } | null;
  budgets: Array<{ roleId: string; role: string; code: string; monthlyTokens: number | null }>;
  providers: Array<{ key: AssistantProvider; label: string; defaultModel: string | null }>;
}

export interface AssistantUsageReport {
  since: string;
  connectedAccounts: number;
  totals: { turns: number; ownTokens: number; companyTokens: number };
  people: Array<{ userId: string; name: string | null; turns: number; ownTokens: number; companyTokens: number; toolCalls: number }>;
}

export interface KeyInput {
  provider: AssistantProvider;
  model?: string;
  apiKey: string;
}

const unwrap = <T,>(r: { data: ApiResponse<T> }) => r.data.data;

export const assistantService = {
  status: async () => unwrap(await apiClient.get<ApiResponse<AssistantStatus>>("/assistant/status")),
  connect: async (body: KeyInput) => unwrap(await apiClient.put<ApiResponse<AssistantStatus>>("/assistant/connection", body)),
  disconnect: async () => unwrap(await apiClient.delete<ApiResponse<AssistantStatus>>("/assistant/connection")),
  chat: async (messages: ChatTurn[]) => unwrap(await apiClient.post<ApiResponse<ChatReply>>("/assistant/chat", { messages })),

  adminSettings: async () => unwrap(await apiClient.get<ApiResponse<AssistantAdminSettings>>("/assistant/admin/settings")),
  updateSettings: async (body: Partial<{ enabled: boolean; byokAllowed: boolean; companyKeyEnabled: boolean; companyModel: string }>) =>
    unwrap(await apiClient.put<ApiResponse<AssistantAdminSettings>>("/assistant/admin/settings", body)),
  setCompanyKey: async (body: KeyInput) => unwrap(await apiClient.put<ApiResponse<AssistantAdminSettings>>("/assistant/admin/company-key", body)),
  removeCompanyKey: async () => unwrap(await apiClient.delete<ApiResponse<AssistantAdminSettings>>("/assistant/admin/company-key")),
  setBudget: async (roleId: string, monthlyTokens: number | null) =>
    unwrap(await apiClient.put<ApiResponse<AssistantAdminSettings>>(`/assistant/admin/budgets/${roleId}`, { monthlyTokens })),
  usage: async () => unwrap(await apiClient.get<ApiResponse<AssistantUsageReport>>("/assistant/admin/usage")),
};

export function tokens(n: number): string {
  return n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}
